import { TrackingEvent } from '../../../types/consultation';
import { pushToDataLayer } from './push-to-data-layer';

//------- TYPES -------------
declare global {
  interface Window {
    google_tag_manager: any;
    dataLayer: TrackingEvent[];
  }
}

export enum TagType {
  HEADING,
  LINK,
  CTA,
  CALL
}

type TrackingDimensions = {
  event: 'cta' | 'textlink' | 'direct_call';
  element_section: string | undefined;
  element_detail?: string;
  link_text?: string | undefined;
  link_url?: string | undefined;
};

//------- Initialisation Code / dom-binding -------------

const isTrackingTest: boolean = window.location.search.indexOf('trackingtest=1') !== -1;

const TRACKED_ELEMENTS_SELECTOR = 'a:not([download]), [data-tr-event], button[data-modal]';
const HEADINGS_SELECTOR = 'h1, h2, h3, h4, h5, h6, .h1, .h2, .h3, .h4, .h5';
const SECTIONS_SELECTOR = 'section, [data-tr-element_section]:not([data-tr-event])';
const MODALS_SELECTOR = '.modal, .popup';

//elements that already got a direct click listener
const elementsWithListener = new WeakSet<HTMLElement>();

document.addEventListener(
  'click',
  (ev: Event) => {
    if (!(ev.target instanceof Element)) {
      return;
    }
    const element = ev.target.closest<HTMLElement>(TRACKED_ELEMENTS_SELECTOR);
    if (!element) {
      return;
    }
    //nested tracked elements (e.g. a button inside a link): if any of them has a direct listener the click is already tracked
    for (let tracked: HTMLElement | null | undefined = element; tracked; tracked = tracked.parentElement?.closest<HTMLElement>(TRACKED_ELEMENTS_SELECTOR)) {
      if (elementsWithListener.has(tracked)) {
        return;
      }
    }
    const tagType = getTagType(element);
    if (tagType !== undefined) {
      trackElementClick(element, tagType, getDimensionsAtClick(element), ev);
    }
  },
  true
);

const sections: NodeListOf<HTMLElement> = document.querySelectorAll(SECTIONS_SELECTOR);
const modals: NodeListOf<HTMLElement> = document.querySelectorAll(MODALS_SELECTOR);

//array to check against to make sure all links and buttons are handled - each link/button will only be handled once
const restLinks: HTMLElement[] = Array.from(document.querySelectorAll(TRACKED_ELEMENTS_SELECTOR));

//Note: section order gets reversed so that deeper nested "sections" are handled before their parent "sections" This way parents wont override their childrens element_section dimension
Array.from(sections)
  .reverse()
  .forEach((section: HTMLElement) => {
    addTrackingToSection(section, getElementSectionFromBlock(section) || '');
  });

modals.forEach((modal: HTMLElement) => {
  addTrackingToSection(modal, getModalSection(modal));
});

//handle all links that weren't covered before / don't have an element_section
addTrackingtoElements(restLinks, 'na');

//------- Logic -------------

function addTrackingToSection(section: HTMLElement, elementSectionValue: string = '') {
  const elements: HTMLElement[] = Array.from(section.querySelectorAll(`${HEADINGS_SELECTOR}, ${TRACKED_ELEMENTS_SELECTOR}`));
  addTrackingtoElements(elements, elementSectionValue);
}

function addTrackingtoElements(elements: HTMLElement[], elementSectionValue: string): void {
  const sectionDimensions: Partial<TrackingDimensions> = {
    element_section: elementSectionValue
  };

  for (const element of elements) {
    const tagType: TagType = getTagType(element) ?? TagType.HEADING;

    switch (tagType) {
      case TagType.HEADING:
        if (isTrackableHeading(element)) {
          sectionDimensions.element_detail = getTextContent(element);
        }
        break;
      case TagType.CALL:
      case TagType.LINK:
      case TagType.CTA:
        const indexInRestLinks = restLinks.indexOf(element);
        //only add tracking if the link wasn't handled yet
        if (indexInRestLinks !== -1) {
          restLinks.splice(restLinks.indexOf(element), 1);
          addTrackingToElement(element, tagType, { ...sectionDimensions, ...getTariffHeaderDimensions(element) });
        }
        break;
      default:
        break;
    }
  }
}

function getTagType(element: HTMLElement): TagType.CTA | TagType.LINK | TagType.CALL | undefined {
  if (element.tagName !== 'A' && element.tagName !== 'BUTTON' && !element.hasAttribute('data-tr-event')) {
    return undefined;
  }
  if (element.tagName === 'A' && element.getAttribute('href')?.startsWith('tel:')) {
    return TagType.CALL;
  }
  if (element.tagName === 'A' && (!element.classList.contains('btn') || element.classList.contains('link'))) {
    return TagType.LINK;
  }
  return TagType.CTA;
}

//same dimensions the init code assigns, but resolved at click time for elements without a direct listener
function getDimensionsAtClick(element: HTMLElement): Partial<TrackingDimensions> {
  const sectionElement = element.closest<HTMLElement>(`${SECTIONS_SELECTOR}, ${MODALS_SELECTOR}, .tariff-table-24-block`);
  if (!sectionElement) {
    return { element_section: 'na' };
  }
  const element_section = (sectionElement.matches(MODALS_SELECTOR) ? getModalSection(sectionElement) : getElementSectionFromBlock(sectionElement)) || 'na';
  //last heading before the element, same as the init code
  const headings = Array.from(sectionElement.querySelectorAll<HTMLElement>(HEADINGS_SELECTOR)).filter(
    heading => isTrackableHeading(heading) && !heading.contains(element) && heading.compareDocumentPosition(element) & Node.DOCUMENT_POSITION_FOLLOWING
  );
  const lastHeading = headings[headings.length - 1];
  return { element_section, ...(lastHeading && { element_detail: getTextContent(lastHeading) }), ...getTariffHeaderDimensions(element) };
}

//CTAs inside a tariff table header card use the tariff name as element_detail (same values as the deductible_interaction event)
function getTariffHeaderDimensions(element: HTMLElement): Partial<TrackingDimensions> {
  //the sticky header renders the CTA next to (not inside) the card, and the name in the card's first div instead of .tariff-name
  const card = element.closest('.tariff-head')?.querySelector<HTMLElement>('.tariff-header-card');
  const tariffName = card?.querySelector<HTMLElement>('.tariff-name') ?? card?.firstElementChild;
  return tariffName instanceof HTMLElement ? { element_detail: getTextContent(tariffName) } : {};
}

function getModalSection(modal: HTMLElement): string {
  return modal.dataset.trElement_section || modal.id || 'na';
}

//headings inside links / buttons / tables don't give reliable results and are excluded
function isTrackableHeading(heading: HTMLElement): boolean {
  return !heading.closest('a, button, .btn, table');
}

export function addTrackingToElement(element: HTMLElement, tagType: TagType.CTA | TagType.LINK | TagType.CALL, dimensions: Partial<TrackingDimensions>): void {
  elementsWithListener.add(element);
  element.addEventListener('click', (ev: Event) => trackElementClick(element, tagType, dimensions, ev), true);
}

function trackElementClick(element: HTMLElement, tagType: TagType.CTA | TagType.LINK | TagType.CALL, dimensions: Partial<TrackingDimensions>, ev: Event): void {
  if (!window['google_tag_manager'] && !isTrackingTest) {
    return;
  }

  const customDimensions: TrackingDimensions = {
    event: tagType === TagType.CALL ? 'direct_call' : tagType === TagType.CTA ? 'cta' : 'textlink',
    element_section: undefined,
    ...dimensions,
    link_text: getTextContent(element)
  };

  const hrefAttribute = element.getAttribute('href');
  const isAnchor = isHrefAnchor(hrefAttribute || '');
  const href = !hrefAttribute ? undefined : isAnchor ? hrefAttribute : (element as HTMLLinkElement).href;
  const link_url: string = href || element.dataset.modal || 'na';

  const redirect = href && !isAnchor && !element.getAttribute('rel') && element.getAttribute('target') !== '_blank';

  // Check if this is a navigation toggle link on a touch device
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0 || (navigator as any).msMaxTouchPoints > 0;
  const isNavToggle = element.closest('.box-mobile > a') && isTouchDevice;
  const shouldActuallyRedirect = redirect && !element.classList.contains('dontRedirect') && !isNavToggle;

  const hasLegacyTracking = element.hasAttribute('category');

  if (redirect) {
    ev.preventDefault();
  }

  //TODO should be defined more strictly
  const ga4Dimensions: {
    [id: string]: any;
  } = { ...customDimensions, ...(link_url && { link_url }) };

  for (const attribute of element.attributes) {
    if (attribute.name.startsWith('data-tr-')) {
      const name = attribute.name.replace('data-tr-', '');
      if (name === 'yob' || name === 'age') {
        const valueAsNumber = parseInt(attribute.value);
        if (!isNaN(valueAsNumber)) {
          ga4Dimensions[name] = valueAsNumber;
        }
      } else {
        ga4Dimensions[name] = attribute.value;
      }
    }
  }

  const callBackDimensions = shouldActuallyRedirect && {
    eventCallback: () => {
      if (!isTrackingTest) {
        document.location = (href || document.location) as string;
      }
    },
    eventTimeout: 300
  };
  pushToDataLayer((hasLegacyTracking ? ga4Dimensions : { ...ga4Dimensions, ...callBackDimensions }) as TrackingEvent);
}

function getElementSectionFromBlock(block: HTMLElement): string | undefined {
  const dataSetSection = block.dataset.trElement_section;
  if (dataSetSection) {
    return dataSetSection;
  }

  const classes = Array.from(block.classList);

  for (const className of classes) {
    if (!['site-block', 'hero'].includes(className)) {
      return className;
    }
  }
}
function getTextContent(element: HTMLElement): string {
  const internalHeading: HTMLElement | null = element.querySelector('h1, h2, h3, h4, h5, h6, .h1, .h2, .h3, .h4, .h5, .heading');
  const contentTag: HTMLElement = internalHeading || element;
  let sanitizedContent: string = (contentTag.innerText || contentTag.textContent || '')
    ?.replace(/<\s*\/?\s*[a-zA-Z][^>]*>/gm, ' ') //remove html tags
    .replace(/\s+/gm, ' ') //replace any whitespace characters (like tabs, ...) and duplicate whitepaces
    .trim();
  if (sanitizedContent.length > 100) {
    sanitizedContent = sanitizedContent.substring(0, 97) + '...';
  }
  return sanitizedContent || element.getAttribute('aria-label') || element.getAttribute('title') || 'na';
}

function isHrefAnchor(href: string): boolean {
  return href.startsWith('#');
}

export {};
